/**
 * MoonX design tokens — the raw brand values, as data.
 *
 * THIS IS THE ONLY FILE IN THE REPOSITORY ALLOWED TO CONTAIN A RAW COLOUR.
 * Every other file uses `var(--mx-…)` or a Mantine prop. The rule is enforced by
 * `no-restricted-syntax` in web/ui/eslint.config.mjs, and one deliberate violation
 * fixture (web/ui/test/fixtures/lint/raw-colour-violation.ts.txt) proves it turns red.
 * docs/ui/02-theme.md, docs/ui/00-README.md.
 *
 * ---------------------------------------------------------------------------------------------
 * KNOWN CONTRAST DEBT — ruled 2026-08-26, do not reopen
 *
 * The light theme takes the LIVE WEBSITE's values verbatim, including two that measure below the
 * WCAG AA readable minimum. This was decided deliberately so the platform and the website stay
 * identical, and it is NOT to be re-litigated screen by screen.
 *
 *   | What                               | Measured    | Standard |
 *   |------------------------------------|-------------|----------|
 *   | ink on a gold button, light theme  | 3.72:1      | 4.5:1    |
 *   | the same on hover                  | 2.76:1      | 4.5:1    |
 *   | `m6` on a light card               | 4.49:1      | 4.5:1    |
 *   | every border, both themes          | 1.04-2.97:1 | 3:1      |
 *
 * When it is fixed, it is fixed in both places at once — here and in the website's own
 * tokens.css — so they never diverge. The prototype already carries a passing value for the gold
 * button (near-white ink, 5.38:1), so the website's fix is a one-line change whenever it is
 * wanted. Until then these numbers are known, accepted, and recorded here rather than
 * rediscovered. (docs/ui/02-theme.md; docs/ui/01-tokens.md;
 * docs/ui/05-open-questions.md and :25-30.)
 *
 * The border row is a whole-palette fact and lives in this header. The two values that carry an
 * inline note below are `light.onAccent` and `light.m6` — the two the debt table names by value.
 * ---------------------------------------------------------------------------------------------
 *
 * WHY EVERY OKLCH STRING BELOW CARRIES A `%`. Mantine's `luminance()` parses OKLCH with
 * /oklch\((.*?)%\s/ — it needs the percent sign AND the space after it. Verified first-hand in
 * the shipped source of @mantine/core 9.5.2:
 *   esm/core/MantineProvider/color-functions/luminance/luminance.mjs
 *     function getLightnessFromOklch(oklchColor) {
 *       const match = oklchColor.match(/oklch\((.*?)%\s/);
 *       return match ? parseFloat(match[1]) : null;
 *     }
 * A unitless lightness — `oklch(0.8 0.15 85)` — silently measures as 0 and `autoContrast` then
 * picks white text on everything. Write `oklch(80% 0.15 85)`, never `oklch(0.8 0.15 85)`.
 */

/**
 * OKLCH channel triplets, no wrapper — composed at use as `oklch(var(--mx-…))`.
 * That indirection is the brand repo's own, and it exists so a transparency can be applied at
 * the point of use: `oklch(var(--mx-accent) / 0.3)`.
 *
 * Values: MoonX_WebSite/src/client/ui/styles/tokens.css, verbatim in both themes, as transcribed
 * into docs/ui/02-theme.md. Ruled 2026-08-26 — see the KNOWN CONTRAST DEBT note above.
 */
export const brand = {
  dark: {
    bg: '8% 0.004 60',
    bg2: '9% 0.004 60',
    surface: '11% 0.004 60',
    surface2: '13% 0.004 60',
    surface4: '18% 0.006 60',
    border: '20% 0.006 60',
    border2: '24% 0.006 60',
    borderDim: '16% 0.006 60',
    borderDash: '32% 0.01 60',
    accent: '80% 0.15 85',
    accentHover: '88% 0.13 85',
    text: '97% 0.002 60',
    onAccent: '10% 0.004 60',
    m1: '70% 0.01 60',
    m2: '65% 0.01 60',
    m3: '62% 0.01 60',
    m4: '60% 0.01 60',
    m5: '58% 0.01 60',
    m6: '55% 0.01 60',
    m7: '52% 0.01 60',
    m8: '50% 0.01 60',
    m9: '45% 0.01 60',
    danger: '66% 0.16 25',
    onDanger: '16% 0.02 20',
    // `success` deliberately does NOT match the §1.2 table's brand value of `76% .10 150`.
    // docs/ui/01-tokens.md recommends the prototype's colourfulness because "the brand's
    // dark green is visibly washed out beside the rest of the palette", and the track's own code
    // block applies it. Q11 and Q12 ruled the light-theme greys and the faint outlines only, so
    // "take the website's values" does not reach `success`. Do not "correct" it back.
    success: '72% 0.16 150',
    warning: '80% 0.13 75',
    // `info` and `onDanger` come from the prototype: the brand repo has neither, and the back
    // office needs an informational colour and an ink for its own danger fill
    // (docs/ui/01-tokens.md).
    info: '72% 0.11 240',
    // `chart1`-`chart11`: a chart's own colours, one per group after the accent - the Audience
    // donut's slices (decision-log row 269, the owner, 2026-10-06). Not the website's: it draws no
    // such chart. Eleven hues at one lightness per theme, light enough to read on the dark card.
    chart1: '70% 0.12 250',
    chart2: '72% 0.14 150',
    chart3: '68% 0.15 25',
    chart4: '70% 0.13 300',
    chart5: '74% 0.11 200',
    chart6: '76% 0.14 55',
    chart7: '72% 0.14 340',
    chart8: '76% 0.12 115',
    chart9: '66% 0.13 275',
    chart10: '76% 0.1 180',
    chart11: '70% 0.12 0',
  },
  light: {
    bg: '97% 0.004 60',
    bg2: '94% 0.004 60',
    surface: '93% 0.004 60',
    surface2: '90% 0.004 60',
    surface4: '85% 0.006 60',
    border: '82% 0.006 60',
    border2: '76% 0.006 60',
    borderDim: '88% 0.006 60',
    borderDash: '65% 0.01 60',
    accent: '52% 0.15 85',
    accentHover: '45% 0.15 85',
    text: '15% 0.006 60',
    // RULED 2026-08-26: match the live website exactly. THIS LINE IS THE DEBT — near-black ink on
    // the light gold button measures 3.72:1, and 2.76:1 against `accentHover`. See the KNOWN
    // CONTRAST DEBT note above. It is deliberate. Do not "fix" it here; it is fixed in this file
    // and in the website's tokens.css together or not at all.
    onAccent: '10% 0.004 60',
    m1: '40% 0.01 60',
    m2: '43% 0.01 60',
    m3: '46% 0.01 60',
    m4: '48% 0.01 60',
    m5: '50% 0.01 60',
    // THE SECOND DEBT VALUE: `m6` on a light card measures 4.49:1 against a 4.5:1 standard — one
    // hundredth short. Its "58 uses" (docs/ui/01-tokens.md) are the PROTOTYPE's; ours has none.
    // Ruled 2026-08-26 with the line above. See the KNOWN CONTRAST DEBT note above.
    m6: '52% 0.01 60',
    m7: '54% 0.01 60',
    m8: '56% 0.01 60',
    m9: '58% 0.01 60',
    danger: '52% 0.18 25',
    onDanger: '99% 0.004 20',
    success: '46% 0.15 150',
    warning: '55% 0.13 75',
    info: '46% 0.14 250',
    // The same eleven hues, dark enough to read on the light card.
    chart1: '55% 0.14 250',
    chart2: '55% 0.14 150',
    chart3: '55% 0.16 25',
    chart4: '55% 0.14 300',
    chart5: '57% 0.11 200',
    chart6: '60% 0.15 55',
    chart7: '57% 0.15 340',
    chart8: '58% 0.13 115',
    chart9: '50% 0.14 275',
    chart10: '58% 0.1 180',
    chart11: '55% 0.13 0',
  },
} as const;

/**
 * One theme's worth of palette. Widened to `string` on purpose: the two buckets have identical
 * KEYS but different literal VALUES, so keying off `typeof brand.dark` directly would make
 * `brand.light` unassignable to it. A test asserts the two key sets stay identical.
 */
export type BrandPalette = { readonly [K in keyof (typeof brand)['dark']]: string };

/**
 * Theme-agnostic motion. From tokens.css as transcribed at docs/ui/02-theme.md,
 * with ONE value changed by a ruling the track's draft predates.
 *
 * `dur` IS 250ms, NOT the 300ms the draft ships. Q16 was answered on 2026-08-26 —
 * "(a) in all three — quicker, no ambient motion, snap to a scale"
 * (docs/ui/05-open-questions.md), and the detail is explicit: "(a) Take the website's quicker
 * speed (recommended) — a console is used all day and snappier wears better"
 * (docs/ui/05-open-questions.md). docs/ui/02-theme.md still writes 300ms and calls it
 * "Question 4"; that marker is one of eight stale per-document markers, and the topic it points
 * at is Q16, which is closed.
 */
export const motion = {
  durFast: '150ms',
  dur: '250ms',
  durSlow: '350ms',
  easeOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeMoon: 'cubic-bezier(0.22, 1, 0.36, 1)',
} as const;

/**
 * tokens.css (dark) and :143-145 (light), verbatim from docs/ui/02-theme.md.
 *
 * BIND THESE NARROWLY (docs/ui/01-tokens.md §6). The cabinet separates surfaces with a lightness
 * step plus a hairline, never a drop shadow — measured at 16 pixel-valued borders for every
 * shadow. `elev-1` is used by nothing in the cabinet; `elev-2` is popovers, dropdowns and menus;
 * `elev-3` is modals and drawers. Nothing else.
 *
 * `boxShadow.button` from the brand repo (`0 2px 20px #FFF6D626`) is deliberately absent: it is a
 * raw hexadecimal with baked-in transparency and it is not theme-aware, so it would be wrong in
 * one of the two themes by construction (docs/ui/01-tokens.md).
 */
export const elevation = {
  dark: {
    1: '0 1px 2px oklch(0% 0 0 / 0.3), 0 2px 5px oklch(0% 0 0 / 0.22), inset 0 1px 0 oklch(100% 0 0 / 0.06)',
    2: '0 4px 10px oklch(0% 0 0 / 0.32), 0 12px 26px oklch(0% 0 0 / 0.28), inset 0 1px 0 oklch(100% 0 0 / 0.06)',
    3: '0 12px 26px oklch(0% 0 0 / 0.4), 0 30px 60px oklch(0% 0 0 / 0.45), inset 0 1px 0 oklch(100% 0 0 / 0.07)',
  },
  light: {
    1: '0 1px 2px oklch(45% 0.02 60 / 0.08), 0 2px 6px oklch(45% 0.02 60 / 0.06)',
    2: '0 4px 10px oklch(45% 0.02 60 / 0.1), 0 12px 26px oklch(45% 0.02 60 / 0.07)',
    3: '0 12px 26px oklch(45% 0.02 60 / 0.12), 0 30px 60px oklch(45% 0.02 60 / 0.1)',
  },
} as const;

/**
 * MoonX gold, ten steps, light -> dark, every one in the `oklch(NN% C H)` PERCENTAGE form.
 *
 * Index 3 and index 7 are the two real brand golds; the rest are the ramp between and beyond
 * them. Index 8 is the light theme's hover value.
 *
 * These tuples live HERE and not in theme.ts, which is where the track drew them
 * (docs/ui/02-theme.md). They are token data, not theme wiring, and theme.ts cannot hold
 * them without breaking the no-raw-colour rule the same document ordains — the draft theme.ts
 * carries 22 raw literals against a rule that forbids any. The rule is stated twice and violated
 * once, in an illustrative code block, so the rule wins and the file moves.
 */
export const gold = [
  'oklch(97% 0.03 85)', // 0
  'oklch(93% 0.06 85)', // 1
  'oklch(88% 0.10 85)', // 2
  'oklch(80% 0.15 85)', // 3  <- brand `accent`, DARK theme
  'oklch(74% 0.15 85)', // 4
  'oklch(68% 0.15 85)', // 5
  'oklch(60% 0.15 85)', // 6
  'oklch(52% 0.15 85)', // 7  <- brand `accent`, LIGHT theme
  'oklch(45% 0.15 85)', // 8  <- brand `accent-hover`, LIGHT theme
  'oklch(38% 0.13 85)', // 9
] as const;

/**
 * Neutral ramp, so `color="gray"` in any Mantine component lands on the brand greys rather than
 * Mantine's blue-tinted default. These are the DARK theme's muted values, and in the dark scheme
 * every gray shade resolves to them.
 *
 * THE LIGHT HALF IS PAID - IN ./global.css, NOT HERE. A theme colour is ONE tuple, Mantine emits
 * its shades once for both schemes in its shared `:root` block, and ./css-variables.ts emits no
 * `--mantine-color-gray-N`. So until 2026-09-24 the light scheme painted THIS ramp on light
 * grounds: its `--mantine-color-dimmed` is `var(--mantine-color-gray-6)` - index 6 below, the DARK
 * theme's `m6` - which axe reported at 4.45:1 on the light page on 2026-09-22, and a hovered table
 * row (gray.1, 70% lightness) left it at 1.82. That was once filed under the ruled `m6` row of the
 * debt table above. The ruling is about the light `m6` TOKEN; this was a dark value reaching a
 * similar number through the wrong ramp - a defect, not the debt. The light block at the end of
 * ./global.css points gray.1, .2 and .5-.9 at light brand steps and leaves gray.0, .3 and .4 (the
 * last is the ruled default border) on this array. Mantine's `virtualColor` could carry a second,
 * light tuple, but only by writing the light palette out again as ten new literals.
 */
export const grey = [
  'oklch(97% 0.002 60)',
  'oklch(70% 0.01 60)',
  'oklch(65% 0.01 60)',
  'oklch(62% 0.01 60)',
  'oklch(60% 0.01 60)',
  'oklch(58% 0.01 60)',
  'oklch(55% 0.01 60)',
  'oklch(52% 0.01 60)',
  'oklch(50% 0.01 60)',
  'oklch(45% 0.01 60)',
] as const;

/** Mantine's `theme.white` / `theme.black`. Colour literals, so they belong here too. */
export const absolute = {
  white: 'oklch(100% 0 0)',
  black: 'oklch(8% 0.004 60)',
} as const;

/**
 * THE MOONX APP'S ARTICLE SCREEN, for the cabinet's article preview (the owner, 2026-10-20;
 * web/authors/src/routes/articles/editor/ArticlePreview.tsx). Not the brand palette - the website
 * preview reads that one - but the iOS app's own: MoonX_iOS `Colors.xcassets` (BlackSet, Text,
 * Tags) and the system blue, as OKLCH triplets. Where the app paints white or black at a fraction
 * (`textSecondaryColor`, `tagBackgroundColor`, the quote bar - `textSecondaryColor` at 0.1 - and the
 * light image placeholder), the value is that blend over the screen's background, made opaque. When
 * the app changes these, they change here.
 */
export const appReading = {
  dark: {
    bg: '20.8% 0.002 145',
    cell: '24.5% 0.002 326',
    stub: '22.6% 0 0',
    divider: '31.3% 0 0',
    text: '100% 0 0',
    secondary: '71.4% 0.002 146',
    tag: '28.5% 0 0',
    quoteBar: '30.4% 0.002 146',
    link: '62.4% 0.206 255',
  },
  light: {
    bg: '96.6% 0.006 60',
    cell: '100% 0 0',
    stub: '89.3% 0.005 68',
    divider: '91.2% 0.006 60',
    text: '14.9% 0.004 107',
    secondary: '59.7% 0.005 78',
    tag: '100% 0 0',
    quoteBar: '93% 0.006 60',
    link: '60.3% 0.218 257',
  },
} as const;

/** The phone the app preview is drawn in, the same in both schemes: its bezel and the stars. */
export const appFrame = {
  bezel: '15% 0.002 286',
  star: '100% 0 0',
} as const;

/**
 * The twelve-step spacing scale plus one page gutter, in pixels.
 *
 * Q16's third part ruled "(a) Snap to a regular scale — one pixel here and there is invisible,
 * and a scale means nobody invents a new gap" (docs/ui/05-open-questions.md and :407-408).
 * Option (b), adding steps at 18 and 26, is REFUSED. Coverage of the drawing is 69.8% exact and
 * 90.3% when odd values snap by one pixel (docs/ui/01-tokens.md).
 */
export const space = [
  '0px',
  '2px',
  '4px',
  '6px',
  '8px',
  '10px',
  '12px',
  '14px',
  '16px',
  '20px',
  '24px',
  '32px',
] as const;

/** The page gutter, which is not a step on the scale. */
export const spacePage = '80px';

/**
 * Radius steps that have no Mantine slot. The ruled set is seven — xs 5, sm 8, md 10, lg 12,
 * xl 16, pill 999, full 50% — and Mantine's five carry the first five (docs/ui/01-tokens.md).
 * The brand's 20/24/28 steps are dropped: zero uses in the cabinet and editor.
 */
export const radiusExtra = {
  pill: '999px',
  full: '50%',
} as const;

/**
 * Type roles with no Mantine slot. `display` and `prose` are reading sizes; `nano` is the
 * cabinet's chip/pill/column-header size and accounts for 14.6% of all its text, which is why it
 * has to be a token rather than a pixel value typed into a screen
 * (docs/ui/01-tokens.md).
 */
export const typeExtra = {
  fsDisplay: 'clamp(1.75rem, 4vw, 2.875rem)',
  fsProse: '1.0625rem',
  lhProse: '1.85',
  fsNano: '0.625rem',
  lsNano: '0.6px',
  lsEyebrow: '0.5px',
  lsTight: '-0.02em',
} as const;
